import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useProfile } from "../context/ProfileContext";
import { detailedReportsPaymentStatus } from "../config/config";
import LogoIcon from "../images/logo.png";
import {
  FaTachometerAlt,
  FaUsers,
  FaChevronDown,
  FaChevronUp,
  FaHeadset,
  FaCar,
  FaUserTag,
  FaMoneyBill,
  FaCog,
  FaCreditCard,
  FaTax,
  FaPercentage,
  FaPhone,
  FaUserSlash,
  FaHandHoldingUsd,
} from "react-icons/fa";

const AdminSidebar = ({ open }) => {
  const location = useLocation();
  const { profile, assignModule } = useProfile();

  const isSuperAdmin = profile?.role?.slug === "superadmin";
  const isNewType = profile?.employeeType === "new";
  const isActive = (path) => location.pathname === path;
  const isDashboardActive = location.pathname.startsWith("/dashboard");
  const showByModule = isNewType && assignModule;
  const showDrivers = isSuperAdmin || !isNewType || (showByModule && assignModule?.driver?.enabled);
  const showRides = isSuperAdmin || !isNewType || (showByModule && assignModule?.rides?.enabled);
  const isCancellationActive = location.pathname.startsWith("/cancellation-suspended");
  const isNumberSharingActive = location.pathname.startsWith("/number-sharing-suspended");
  const isCallBlastingActive = location.pathname.startsWith("/call-blasting");
  const showTaxCommission = isSuperAdmin || !isNewType || (showByModule && assignModule?.taxesCommission?.enabled);
  const [activeMenu, setActiveMenu] = useState(
    location.pathname.startsWith("/dashboard") ? "dashboard" : null
  );

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  return (
    <aside
      className={`mobileview overflow-y-auto no-scrollbar w-64 sidebar-gradient text-white h-screen transform transition-all duration-300 fixed md:relative z-10 shadow-2xl border-r border-slate-600 ${open ? "hidden mobileshow" : "translate-x-0"
        }`}
    >
      {/* Sidebar */}
      <div className="p-6">
        {/* Logo Section */}
        <div className="mb-3 text-center pb-6">
          <div className="mx-auto h-16 w-16 overflow-hidden rounded-full">
            <img
              src={LogoIcon}
              alt="logo"
              className="h-full w-full object-cover object-center"
            />
          </div>
          <h2 className="text-lg font-bold text-white">UDrivo Admin</h2>
          <p className="text-xs text-slate-300">Management Portal</p>
        </div>

        {/* new users dont access dashboard */}
        
        <nav className="space-y-2">
          {isSuperAdmin && (
            <div className="mb-2">
              <div
                onClick={() => toggleMenu("dashboard")}
                className={`group flex items-center px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 menu-item-hover ${activeMenu === "dashboard" || isDashboardActive
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg transform scale-105 active-menu-item"
                    : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                  }`}
              >
                <FaTachometerAlt className="mr-3 text-lg" />
                <span className="font-medium">Dashboard</span>
                <span className="ml-auto transition-transform duration-200">
                  {activeMenu === "dashboard" ? (
                    <FaChevronUp className="text-sm" />
                  ) : (
                    <FaChevronDown className="text-sm" />
                  )}
                </span>
              </div>
              {activeMenu === "dashboard" && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-slate-600 pl-4">
                  <Link
                    to="/dashboard"
                    className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>Overview</span>
                  </Link>
                  <Link
                    to="/dashboard/trips-metrics"
                    className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>Trips Metrics</span>
                  </Link>
                  <Link
                    to="/dashboard/drivers-metrics"
                    className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>Drivers Metrics</span>
                  </Link>
                </div>
              )}
            </div>
          )}
          {/* Users Menu - hidden for new type (they get standalone Drivers link below) */}
          {!isNewType && (
            <div className="mb-2">
              <div
                onClick={() => toggleMenu("users")}
                className={`group flex items-center px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 ${activeMenu === "users"
                    ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105"
                    : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                  }`}
              >
                <FaUsers className="mr-3 text-lg" />
                <span className="font-medium">Users</span>
                <span className="ml-auto transition-transform duration-200">
                  {activeMenu === "users" ? (
                    <FaChevronUp className="text-sm" />
                  ) : (
                    <FaChevronDown className="text-sm" />
                  )}
                </span>
              </div>
              {activeMenu === "users" && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-slate-600 pl-4">
                  {isSuperAdmin && (
                    <Link
                      to="/admins"
                      className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    >
                      <span>Admins</span>
                    </Link>
                  )}
                  {isSuperAdmin && (
                    <Link
                      to="/employees"
                      className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    >
                      <span>Employees</span>
                    </Link>
                  )}
                  {isSuperAdmin && (
                    <Link
                      to="/employees/new"
                      className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    >
                      <span>New Employees</span>
                    </Link>
                  )}
                  {isSuperAdmin && (
                    <Link
                      to="/users"
                      className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    >
                      <span>Users</span>
                    </Link>
                  )}
                  <Link
                    to="/drivers"
                    className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>Drivers</span>
                  </Link>
                  <Link
                    to="/drivers/duplicate-pan"
                    // className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    className="hidden px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>Duplicate PAN Drivers</span>
                  </Link>
                  <Link
                    to="/drivers/duplicate-aadhaar"
                    // className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    className="hidden px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>Duplicate Aadhaar Drivers</span>
                  </Link>
                  <Link
                    to="/drivers/duplicate-dl"
                    // className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    className="hidden px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>Duplicate Driving License Drivers</span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Standalone Drivers link for new type (only when driver module enabled) */}
          {isNewType && assignModule?.driver?.enabled && (
            <>
              <Link
                to="/drivers"
                onClick={() => toggleMenu("Drivers")}
                className={`group flex items-center px-4 py-3 rounded-xl transition-all duration-200 mb-2 ${activeMenu === "Drivers"
                    ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg transform scale-105"
                    : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                  }`}
              >
                <FaCar className="mr-3 text-lg" />
                <span className="font-medium">Drivers</span>
              </Link>
            </>
          )}

          {/* Ride Management Menu - hidden for new type unless rides module enabled */}
          {showRides && (
            <div className="mb-2">
              <div
                onClick={() => toggleMenu("rides")}
                className={`group flex items-center px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 ${activeMenu === "rides"
                    ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg transform scale-105"
                    : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                  }`}
              >
                <FaCar className="mr-3 text-lg" />
                <span className="font-medium">Rides</span>
                <span className="ml-auto transition-transform duration-200">
                  {activeMenu === "rides" ? (
                    <FaChevronUp className="text-sm" />
                  ) : (
                    <FaChevronDown className="text-sm" />
                  )}
                </span>
              </div>
              {activeMenu === "rides" && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-slate-600 pl-4">
                  <Link
                    to="/rides"
                    className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>All Rides</span>
                  </Link>
                  <Link
                    to="/ride-statistics"
                    className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>Analytics</span>
                  </Link>
                  {detailedReportsPaymentStatus && (
                    <Link
                      to="/ride-update-start-time"
                      className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    >
                      <span>Update Ride Start Time</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {showRides && (
            <div className="mb-2">
              <div
                onClick={() => toggleMenu("cancellationSuspended")}
                className={`group flex items-center px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 ${activeMenu === "cancellationSuspended" || isCancellationActive
                    ? "bg-gradient-to-r from-rose-500 to-rose-600 text-white shadow-lg transform scale-105"
                    : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                  }`}
              >
                <FaUserSlash className="mr-3 text-lg" />
                <span className="font-medium">Cancelation &amp; Suspended</span>
                <span className="ml-auto transition-transform duration-200">
                  {activeMenu === "cancellationSuspended" || isCancellationActive ? (
                    <FaChevronUp className="text-sm" />
                  ) : (
                    <FaChevronDown className="text-sm" />
                  )}
                </span>
              </div>
              {(activeMenu === "cancellationSuspended" || isCancellationActive) && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-slate-600 pl-4">
                  <Link
                    to="/cancellation-suspended/customers"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${location.pathname === "/cancellation-suspended/customers"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                      }`}
                  >
                    <span>Customer</span>
                  </Link>
                  <Link
                    to="/cancellation-suspended/drivers"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${location.pathname === "/cancellation-suspended/drivers"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                      }`}
                  >
                    <span>Driver</span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {showRides && (
            <div className="mb-2">
              <div
                onClick={() => toggleMenu("numberSharingSuspended")}
                className={`group flex items-center px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 ${activeMenu === "numberSharingSuspended" || isNumberSharingActive
                    ? "bg-gradient-to-r from-violet-500 to-violet-600 text-white shadow-lg transform scale-105"
                    : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                  }`}
              >
                <FaPhone className="mr-3 text-lg" />
                <span className="font-medium">Number Sharing Suspended</span>
                <span className="ml-auto transition-transform duration-200">
                  {activeMenu === "numberSharingSuspended" || isNumberSharingActive ? (
                    <FaChevronUp className="text-sm" />
                  ) : (
                    <FaChevronDown className="text-sm" />
                  )}
                </span>
              </div>
              {(activeMenu === "numberSharingSuspended" || isNumberSharingActive) && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-slate-600 pl-4">
                  <Link
                    to="/number-sharing-suspended/customers"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${location.pathname === "/number-sharing-suspended/customers"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                      }`}
                  >
                    <span>Customer</span>
                  </Link>
                  <Link
                    to="/number-sharing-suspended/drivers"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${location.pathname === "/number-sharing-suspended/drivers"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                      }`}
                  >
                    <span>Driver</span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {isSuperAdmin && (
            <Link
              to="/roles"
              onClick={() => toggleMenu("Roles")}
              className={`group flex items-center px-4 py-3 rounded-xl transition-all duration-200 mb-2 ${activeMenu === "Roles"
                  ? "bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg transform scale-105"
                  : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                }`}
            >
              <FaUserTag className="mr-3 text-lg" />
              <span className="font-medium">Roles</span>
            </Link>
          )}
          {/* Issues Menu - hidden for new type */}
          {!isNewType && (
            <div className="mb-2">
              <div
                onClick={() => toggleMenu("issues")}
                className={`group flex items-center px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 ${activeMenu === "issues"
                    ? "bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg transform scale-105"
                    : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                  }`}
              >
                <FaHeadset className="mr-3 text-lg" />
                <span className="font-medium">Issues</span>
                <span className="ml-auto transition-transform duration-200">
                  {activeMenu === "issues" ? (
                    <FaChevronUp className="text-sm" />
                  ) : (
                    <FaChevronDown className="text-sm" />
                  )}
                </span>
              </div>
              {activeMenu === "issues" && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-slate-600 pl-4">
                  <Link
                    to="/issues"
                    className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                  >
                    <span>All Issues</span>
                  </Link>
                  {profile?.role?.slug === "junior" && (
                    <Link
                      to="/my-assigned-issues"
                      className="block px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-600 rounded-lg transition-colors duration-150"
                    >
                      <span>My Assigned Issues</span> 
                      {/* This link is only visible to junior role, as per backend logic that allows juniors to view only their assigned issues */}
                      
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}

          {isSuperAdmin && (
            <Link
              to="/ratecard"
              onClick={() => toggleMenu("Rate Card")}
              className={`group flex items-center px-4 py-3 rounded-xl transition-all duration-200 mb-2 ${activeMenu === "Rate Card"
                  ? "bg-gradient-to-r from-pink-500 to-pink-600 text-white shadow-lg transform scale-105"
                  : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                }`}
            >
              <FaMoneyBill className="mr-3 text-lg" />
              <span className="font-medium">Rate Card</span>
            </Link>
          )}

          {isSuperAdmin && (
            <Link
              to="/settings"
              onClick={() => toggleMenu("Settings")}
              className={`group flex items-center px-4 py-3 rounded-xl transition-all duration-200 mb-2 ${activeMenu === "Settings"
                  ? "bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-lg transform scale-105"
                  : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                }`}
            >
              <FaCog className="mr-3 text-lg" />
              <span className="font-medium">Settings</span>
            </Link>
          )}

          {!isNewType && (
            <Link
              to="/subscription-management"
              onClick={() => toggleMenu("Subscriptions")}
              className={`group flex items-center px-4 py-3 rounded-xl transition-all duration-200 mb-2 ${activeMenu === "Subscriptions"
                  ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg transform scale-105"
                  : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                }`}
            >
              <FaCreditCard className="mr-3 text-lg" />
              <span className="font-medium">Subscriptions</span>
            </Link>
          )}

          {showTaxCommission && (
            <Link
              to="/tax-commission"
              onClick={() => toggleMenu("TaxCommission")}
              className={`group flex items-center px-4 py-3 rounded-xl transition-all duration-200 mb-2 ${activeMenu === "TaxCommission"
                  ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg transform scale-105"
                  : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                }`}
            >
              <FaPercentage className="mr-3 text-lg" />
              <span className="font-medium">Tax & Commission</span>
            </Link>
          )}

          {showTaxCommission && (
            <Link
              to="/joining-fee"
              onClick={() => toggleMenu("JoiningFee")}
              className={`group flex items-center px-4 py-3 rounded-xl transition-all duration-200 mb-2 ${activeMenu === "JoiningFee" || isActive("/joining-fee")
                  ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg transform scale-105"
                  : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                }`}
            >
              <FaHandHoldingUsd className="mr-3 text-lg" />
              <span className="font-medium">Joining Fee</span>
            </Link>
          )}

          {isSuperAdmin && (
            <div className="mb-2">
              <div
                onClick={() => toggleMenu("CallBlasting")}
                className={`group flex items-center px-4 py-3 rounded-xl cursor-pointer transition-all duration-200 ${
                  activeMenu === "CallBlasting" || isCallBlastingActive
                    ? "bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg transform scale-105"
                    : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
                }`}
              >
                <FaPhone className="mr-3 text-lg" />
                <span className="font-medium">Call Blasting</span>
                <span className="ml-auto transition-transform duration-200">
                  {activeMenu === "CallBlasting" || isCallBlastingActive ? (
                    <FaChevronUp className="text-sm" />
                  ) : (
                    <FaChevronDown className="text-sm" />
                  )}
                </span>
              </div>
              {(activeMenu === "CallBlasting" || isCallBlastingActive) && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-slate-600 pl-4">
                  <Link
                    to="/call-blasting/status"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${
                      location.pathname === "/call-blasting/status" ||
                      location.pathname === "/call-blasting"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                    }`}
                  >
                    <span>Blast by Status</span>
                  </Link>
                  <Link
                    to="/call-blasting/flow"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${
                      location.pathname === "/call-blasting/flow"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                    }`}
                  >
                    <span>Call Flow</span>
                  </Link>
                  <Link
                    to="/call-blasting/location"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${
                      location.pathname === "/call-blasting/location"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                    }`}
                  >
                    <span>Location Not Captured</span>
                  </Link>
                  <Link
                    to="/call-blasting/joining-fee-pending"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${
                      location.pathname === "/call-blasting/joining-fee-pending"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                    }`}
                  >
                    <span>Joining Fee Pending</span>
                  </Link>
                  <Link
                    to="/call-blasting/users-registered"
                    className={`block px-3 py-2 text-sm rounded-lg transition-colors duration-150 ${
                      location.pathname === "/call-blasting/users-registered"
                        ? "text-white bg-slate-600"
                        : "text-slate-300 hover:text-white hover:bg-slate-600"
                    }`}
                  >
                    <span>Users Registered</span>
                  </Link>
                </div>
              )}
            </div>
          )}

          {isSuperAdmin && (
            <Link
              to="/available-drivers"
              className={`group flex items-center px-4 py-3 rounded-xl transition-all duration-200 mb-2 ${
                isActive("/available-drivers")
                  ? "bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg transform scale-105"
                  : "text-slate-300 hover:bg-slate-600 hover:text-white hover:transform hover:scale-105"
              }`}
            >
              <FaUsers className="mr-3 text-lg" />
              <span className="font-medium">Available Drivers</span>
            </Link>
          )}

          {/* Bottom Section */}
          <div className="mt-8 pt-6 border-t border-slate-600">
            <div className="text-center text-xs text-slate-400">
              <p>© 2026 UDrivo Admin</p>
              <p className="mt-1">v2.0.0</p>
            </div>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AdminSidebar;
